import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Typography,
  List,
  ListItem,
  ListItemText,
  IconButton,
  Box,
  Divider,
  useTheme,
  Paper,
} from '@mui/material';
import { X as CloseIcon, Rss } from 'lucide-mui';
import { useTranslation } from 'react-i18next';
import { changelogItems } from './changelogData';

interface ChangelogProps {
  open: boolean;
  onClose: () => void;
}

export const Changelog: React.FC<ChangelogProps> = ({ open, onClose }) => {
  const theme = useTheme();
  const { t } = useTranslation();

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: theme.shape.borderRadius * 1.5,
          background: theme.palette.background.default,
        },
      }}
    >
      <DialogTitle sx={{ px: 4, pt: 3, pb: 2 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Box display="flex" alignItems="center" gap={1}>
            <Rss
              sx={{
                width: 20,
                height: 20,
                color: theme.palette.primary.main,
              }}
            />
            <Typography variant="h5" fontWeight="bold">
              {t('changelog.title')}
            </Typography>
          </Box>
          <IconButton
            onClick={onClose}
            size="small"
            aria-label={t('changelog.close')}
            sx={{
              color: 'text.secondary',
              '&:hover': {
                color: 'text.primary',
                backgroundColor: theme.palette.action.hover,
              },
            }}
          >
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      <Divider />
      <DialogContent sx={{ p: theme.spacing(3) }}>
        {changelogItems.map((item) => (
          <Paper
            key={item.version}
            elevation={0}
            sx={{
              p: 2,
              mb: 2,
              backgroundColor: theme.palette.background.paper,
              borderRadius: theme.shape.borderRadius,
              '&:last-child': { mb: 0 },
            }}
          >
            <Box display="flex" alignItems="baseline" gap={1} mb={1}>
              <Typography
                variant="h6"
                color="primary"
                fontWeight="bold"
                sx={{ letterSpacing: 0.5 }}
              >
                {t('changelog.version', { version: item.version })}
              </Typography>
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ fontStyle: 'italic' }}
              >
                {item.date}
              </Typography>
            </Box>
            <List dense sx={{ pt: 0 }}>
              {item.changes.map((change, index) => (
                <ListItem
                  key={index}
                  sx={{
                    pl: 1,
                    py: 0.5,
                  }}
                >
                  <ListItemText
                    primary={change}
                    primaryTypographyProps={{
                      sx: {
                        fontSize: '0.95rem',
                        lineHeight: 1.5,
                      },
                    }}
                  />
                </ListItem>
              ))}
            </List>
          </Paper>
        ))}
      </DialogContent>
    </Dialog>
  );
};

export default Changelog;
